import React, { Suspense, useCallback } from "react";
import { type CommentsExportEntityType, trackCommentsExported } from "../../analytics";
import { MessageComposer, type MessageComposerProps } from "../features/message-draft";
import { Text } from "../primitives";
import { Comments, CommentsProps } from "../ui/Comments";
import { Spinner } from "../ui/Spinner";

const PDFDownloadButton = React.lazy(() => import("../ui/PDFComments/PDFDownloadButton"));

type CommentsSectionProps = Omit<CommentsProps, "composer"> & {
	entityType: "Account" | "Bond";
	/**
	 * What the comments belong to. Separate from `entityType`, which is a display
	 * label -- a bond request also renders as "Bond" but is its own entity.
	 */
	exportEntityType: CommentsExportEntityType;
	headerEnd?: React.ReactNode;
	commentDownloadHeader?: string;
	downloadTrackingId?: string;
	draftKey: MessageComposerProps["draftKey"];
	createComment: MessageComposerProps["createComment"];
	createCommentLoading: boolean;
};

export const CommentsSection = (props: CommentsSectionProps) => {
	const { exportEntityType, comments } = props;
	const handleExported = useCallback(
		() => trackCommentsExported(exportEntityType, comments?.length ?? 0),
		[exportEntityType, comments],
	);

	return (
		<div className="flex flex-col w-full space-y-[20px]">
			<div className="flex items-center w-full h-fit min-h-fit">
				<Text type="title" color="primary" className="flex-1">
					{props.entityType} Messages
				</Text>
				{!!props.headerEnd && props.headerEnd}
				{props.comments && props.comments.length > 0 && (
					<div className="flex pl-3">
						<Suspense
							fallback={<Spinner containerClassName="w-4 h-4" spinnerClassName="w-4 h-4" />}
						>
							<PDFDownloadButton
								fileName="comments"
								comments={props.comments}
								commentDownloadHeader={props.commentDownloadHeader}
								trackingId={props.downloadTrackingId}
								onExported={handleExported}
							/>
						</Suspense>
					</div>
				)}
			</div>
			<Comments
				comments={props.comments}
				commentsLoading={props.commentsLoading}
				composer={
					<MessageComposer
						draftKey={props.draftKey}
						createComment={props.createComment}
						sending={props.createCommentLoading}
						placeholder="Type here to add a message..."
					/>
				}
			/>
		</div>
	);
};
